import { Dispatch, SetStateAction, useContext, useState } from "react"

import { ApplicationContext } from "../../../../components/submitApplication/single/applicationProvider"
import {
    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE,
    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING,
} from "../../../../constants/generalConstants"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { scrollToTop } from "../../../../utils/submitApplication/submitApplicationUtils"
import { postClientProtected } from "../../clients/clients"
import { getSchemaMapper } from "../../mappers/singleApplication/getSchemaMapper"
import { isSubmitApplicationResponse } from "../../types/singleApplication/submitApplictionTypes"
import {
    CREATE_APPLICATION,
    INSTALLER_SUBMIT_AMENDMENT,
} from "../../urls/hybrid"
import { useClearHasDrafts } from "../installerInterface/useHasDrafts"

// PBI 71845: API-Hybrid connection_application_v1 rejects malformed V2G
// applications with a wrapped HTTPException carrying a detail string that
// embeds one of these markers. The wrapping happens at
// validate_connection_application -> chosen_schema(**body) which catches
// Pydantic ValidationError and re-raises return_bad_request_response(...),
// producing:
//   status: 400
//   body.detail: "Bad request. Detail: There was an error with your
//     application payload. Here are the technical details: ... Value error,
//     <CODE>: <message> ..."
// We surface tailored copy so installers know what to do next.
const V2G_INVERTER_NOT_ALLOWED = "V2G_INVERTER_NOT_ALLOWED"
const V2G_EVCP_NOT_REGISTERED = "V2G_EVCP_NOT_REGISTERED"
const V2G_EVCP_NOT_LINKED = "V2G_EVCP_NOT_LINKED"

const V2G_VALIDATION_MESSAGES: Record<string, string> = {
    [V2G_EVCP_NOT_LINKED]:
        "This EVCP isn't linked to a V2G inverter in the ENA register." +
        " Please ask the manufacturer to update the link in Connect Direct," +
        " or select another EVCP.",
    [V2G_EVCP_NOT_REGISTERED]:
        "V2G applications require an EVCP_AC from the ENA device register." +
        " Please pick one from the search rather than entering it manually.",
    [V2G_INVERTER_NOT_ALLOWED]:
        "Unexpected V2G inverter entry \u2014 the inverter is now resolved" +
        " automatically. Please refresh and resubmit.",
}

/**
 * Inspect a thrown axios error for a V2G validator marker emitted by
 * connection_application_v1. Returns the matched marker code plus tailored
 * display copy, or null when the error is not a recognised V2G rejection.
 *
 * The API returns these as 400s (not 422s): the endpoint catches Pydantic's
 * ValidationError and re-raises HTTPException(400, detail="...<wrapped>...").
 * The marker is embedded in the wrapped string, so we substring-match on
 * `<CODE>:` rather than relying on a strict prefix.
 */
export const extractV2gValidationError = (
    err: unknown
): { code: string; message: string } | null => {
    const response = (err as { response?: { status?: number; data?: unknown } })
        ?.response
    if (response?.status !== 400) {
        return null
    }
    const detail = (response.data as { detail?: unknown })?.detail
    const collectStrings = (): string[] => {
        if (Array.isArray(detail)) {
            return detail.flatMap((entry) => {
                const msg = (entry as { msg?: unknown })?.msg
                return typeof msg === "string" ? [msg] : []
            })
        }
        if (typeof detail === "string") {
            return [detail]
        }
        return []
    }
    const candidates: string[] = collectStrings()
    const codes = [
        V2G_INVERTER_NOT_ALLOWED,
        V2G_EVCP_NOT_REGISTERED,
        V2G_EVCP_NOT_LINKED,
    ]
    const matched = codes.find((code) =>
        // Anchor on the Pydantic v2 ValueError wrapper prefix so a free-text
        // 400 that happens to contain a marker substring (e.g. echoed user
        // input) cannot false-positive. All three codes are raised from
        // model_validator(mode="before") via `raise ValueError("<CODE>: ...")`
        // which Pydantic v2 wraps as `Value error, <CODE>: ...`.
        candidates.some((msg) => msg.includes(`Value error, ${code}:`))
    )
    return matched
        ? { code: matched, message: V2G_VALIDATION_MESSAGES[matched] }
        : null
}

interface ISubmitApplication {
    isSubmitting: boolean
    setIsSubmitting: Dispatch<SetStateAction<boolean>>
    isSuccessDialogOpen: boolean
    setIsMpanNotFoundDialogOpen: Dispatch<SetStateAction<boolean>>
    isMpanNotFoundDialogOpen: boolean
    error: IAppError | undefined
    postApplication: (
        rejectDuplicate?: boolean,
        manuallyProvidedMpan?: string
    ) => Promise<void>
    dno: string
    applicationId: string
    isConfirmDialogOpen: boolean
    setIsConfirmDialogOpen: Dispatch<SetStateAction<boolean>>
    duplicateDetails: object | null
    submissionDetail: string
}

/**
 * Provides functionality for submitting a single connections application.
 * @returns {ISubmitApplication} - loading and error states.
 */
const useSubmitApplication = (): ISubmitApplication => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [dno, setDno] = useState<string>("")
    const [applicationId, setApplicationId] = useState<string>("")
    const [submissionDetail, setSubmissionDetail] = useState<string>("")
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
    const [isMpanNotFoundDialogOpen, setIsMpanNotFoundDialogOpen] =
        useState(false)
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const [duplicateDetails, setDuplicateDetails] = useState<object | null>(
        null
    )

    const {
        formData,
        fixedInputs,
        rawApplicationSpec,
        isAmendmentMode,
        amendmentApplicationId,
        manuallyProvidedMpan,
        setManuallyProvidedMpan,
    } = useContext(ApplicationContext)
    const { draftId } = useContext(ApplicationContext) // Access draftId from context
    const clearHasDrafts = useClearHasDrafts()
    const postApplication = async (
        rejectDuplicate?: boolean,
        manuallyProvidedMpanInput?: string
    ): Promise<void> => {
        let formDataForSubmission

        // Handle manually provided MPAN (if available)
        if (manuallyProvidedMpanInput || manuallyProvidedMpan) {
            const mpanValue = manuallyProvidedMpanInput || manuallyProvidedMpan
            setManuallyProvidedMpan(mpanValue)
            formDataForSubmission = {
                ...formData,
                installerCustomerDetails: {
                    ...formData.installerCustomerDetails,
                    mpan: mpanValue,
                },
            }
        } else {
            formDataForSubmission = formData
        }

        try {
            setIsSubmitting(true)

            // Prepare data for submission (excluding draftId from body)
            const submitData = {
                ...formDataForSubmission,
                ...fixedInputs,
            }

            const body = getSchemaMapper.splitMergedProperties(
                { ...submitData },
                rawApplicationSpec
            )

            // Construct the URL with draftId as a query param (if it exists)
            const url = draftId
                ? `${CREATE_APPLICATION}?draftId=${encodeURIComponent(
                      draftId
                  )}&rejectDuplicate=${rejectDuplicate}`
                : `${CREATE_APPLICATION}?rejectDuplicate=${rejectDuplicate}`

            let res

            // Amendment flow
            if (isAmendmentMode && amendmentApplicationId) {
                res = await postClientProtected(
                    INSTALLER_SUBMIT_AMENDMENT(amendmentApplicationId),
                    body
                )
                if (isSubmitApplicationResponse(res)) {
                    setDno(res.dno)
                    setApplicationId(amendmentApplicationId)
                    setSubmissionDetail(res.detail)
                }
            } else {
                // Creation flow
                res = await postClientProtected(url, body)
                // The API deletes the submitted draft
                if (draftId) clearHasDrafts()
                if (isSubmitApplicationResponse(res)) {
                    setDno(res.dno)
                    setApplicationId(res.applicationId)
                    setSubmissionDetail(res.detail)
                }
            }

            setIsMpanNotFoundDialogOpen(false)
            setIsSuccessDialogOpen(true)
            setError(undefined)
            scrollToTop(document)
        } catch (err) {
            // PBI 71845: V2G connection-application validators on API-Hybrid
            // emit 400s with marker-prefixed detail strings (wrapped by
            // return_bad_request_response). Take precedence over the generic
            // error path so installers see actionable copy.
            const v2gError = extractV2gValidationError(err)
            if (v2gError) {
                const v2gAppError: IAppError = {
                    message: v2gError.code,
                    code: 400,
                    displayDetail: v2gError.message,
                    technicalDetail: v2gError.code,
                    level: "warning",
                }
                setError(v2gAppError)
                return
            }

            const errorObj = parseErrorAsObj(err)
            if (errorObj?.code !== 409) {
                setError(errorObj)
            }

            // We want to close the MPAN dialog on the "review" point of the
            // journey if we've submitted the application from it, and it
            // returns an error which isn't related to the MPAN number.
            // This will enable the user to see the error at "review"

            if (
                isMpanNotFoundDialogOpen &&
                errorObj.code !== UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE &&
                errorObj.code !==
                    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING
            ) {
                setIsMpanNotFoundDialogOpen(false)
            }

            if (
                errorObj.code === UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE ||
                errorObj.code ===
                    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING
            ) {
                setIsMpanNotFoundDialogOpen(true)
            }

            if (errorObj.code === 409) {
                // Handle 409 Conflict error
                const duplicates =
                    (err as any)?.response?.data?.detail?.duplicates ?? null
                setDuplicateDetails(duplicates)

                setIsConfirmDialogOpen(true) // Open the confirm dialog
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return {
        isSubmitting,
        setIsSubmitting,
        isSuccessDialogOpen,
        setIsMpanNotFoundDialogOpen,
        isMpanNotFoundDialogOpen,
        error,
        postApplication,
        dno,
        applicationId,
        isConfirmDialogOpen,
        setIsConfirmDialogOpen,
        duplicateDetails,
        submissionDetail,
    }
}

export default useSubmitApplication
